import React, { useState } from "react";
import { Box, Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import {
  MeasurementUnit,
  MEASUREMENT_UNIT_OPTIONS,
  MEASUREMENT_UNITS,
} from "../../../../common/constants/measurementUnits";
import CustomSelect from "../common/CustomSelect";
import { useAuth } from "../../../../providers/AuthProvider";
import { updateAffiliateUnitType } from "../../../../common/network/Api";

const MeasurementUnitSettings = () => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();

  // Convert numeric unitType (1 = metric, 2 = imperial) to string type
  const getInitialValue = (): MeasurementUnit => {
    if (AuthUser.unitType === 1) return MEASUREMENT_UNITS.metric.value;
    if (AuthUser.unitType === 2) return MEASUREMENT_UNITS.imperial.value;
    return MEASUREMENT_UNITS.metric.value; // default
  };

  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(
    getInitialValue()
  );
  const [isLoading, setIsLoading] = useState(false);

  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASGetUserInfo,
  } = useAppStore();
  const { AuthSetUser } = useAuth();

  const handleChange = async (newValue: MeasurementUnit) => {
    // Convert string value to numeric type for API (1 = metric, 2 = imperial)
    const numericValue =
      MEASUREMENT_UNIT_OPTIONS.find((opt) => opt.value === newValue)
        ?.numericValue || 1;

    setIsLoading(true);
    try {
      await updateAffiliateUnitType(numericValue);
      setMeasurementUnit(newValue);

      // Refresh user info to get updated unitType
      const updatedUser = await ASGetUserInfo(AuthUser);
      await AuthSetUser(updatedUser);

      // Show success snackbar
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Settings Updated",
        message: "Measurement system updated",
      });
      ASSetTypeSnackBar("success");
    } catch (error) {
      console.error("Error updating measurement unit:", error);
      // Show error snackbar
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update measurement system",
      });
      ASSetTypeSnackBar("alert");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.contentRow}>
        <Box sx={styles.labelSection}>
          <Box sx={styles.titleWrapper(theme)}>Measurement system</Box>
          <Box sx={styles.subtitleWrapper(theme)}>
            Applies across all platform features
          </Box>
        </Box>

        <Box sx={styles.selectWrapper}>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={56}
              sx={{ borderRadius: "8px" }}
            />
          ) : (
            <CustomSelect
              label="Unit"
              options={MEASUREMENT_UNIT_OPTIONS.map((option) => option.label)}
              value={
                MEASUREMENT_UNIT_OPTIONS.find(
                  (opt) => opt.value === measurementUnit
                )?.label || null
              }
              onChange={(label) => {
                const selectedOption = MEASUREMENT_UNIT_OPTIONS.find(
                  (opt) => opt.label === label
                );
                if (selectedOption) {
                  handleChange(selectedOption.value);
                }
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MeasurementUnitSettings;

const styles = {
  container: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colorTokens.N[50]}`, // #E1E3E5
    borderRadius: "8px",
    padding: "16px",
    width: "100%",
  }),
  contentRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    width: "100%",
  },
  labelSection: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    gap: "4px",
  },
  titleWrapper: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90], // #3B3F42
  }),
  subtitleWrapper: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80], // #6D7075
  }),
  selectWrapper: {
    width: "366px",
    flexShrink: 0,
  },
};
